import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  Component,
  contentChildren,
  effect,
  inject,
  Injector,
  input,
  output,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  NgSelectOption,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-validated-dropdown',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './validated-dropdown.component.html',
  styleUrl: './validated-dropdown.component.scss',
})
export class ValidatedDropdownComponent<T> {
  private readonly injector = inject(Injector);

  label = input.required<string>();
  inputFormControlName = input.required<string>();
  inputFormControl = input.required<FormControl>();
  validationTrainslations = input<{ [key: string]: string }>();
  leaveSpaceForValidation = input<boolean>(false);
  haveEmptyOption = input<boolean>(true);
  disabled = input<boolean>(false);
  inlineLabel = input<boolean>(false);
  loading = input<boolean>(false);
  optionsStamp = input<unknown>(undefined);
  compareBy = input<((option: unknown, value: unknown) => boolean) | undefined>(
    undefined,
  );

  selectionChanged = output<T>();
  protected optionNodes = contentChildren(NgSelectOption, { descendants: true });
  protected compareWith = (option: unknown, value: unknown): boolean => {
    const custom = this.compareBy();
    return custom
      ? custom(option, value)
      : this.defaultCompareByIdOrStrict(option, value);
  };

  hasRequiredValidator(): string | boolean {
    return this.inputFormControl().hasValidator(Validators.required);
  }

  errorKeys(): string[] {
    const control = this.inputFormControl();
    return control.errors ? Object.keys(control.errors) : [];
  }

  getErrorMessage(errorKey: string): string {
    return this.validationTrainslations()?.[errorKey] ?? errorKey;
  }

  onSelectChange(): void {
    this.selectionChanged.emit(this.inputFormControl().value as T);
  }

  private syncDisabled = effect(() => {
    const ctrl = this.inputFormControl();
    const shouldDisable = this.disabled() || this.loading();
    if (shouldDisable) {
      ctrl.disable({ emitEvent: false });
    } else {
      ctrl.enable({ emitEvent: false });
    }
  });

  private readonly reconcileSelectionAfterOptionsChange = effect((onCleanup) => {
    this.optionNodes();
    this.optionsStamp();
    this.loading();
    const control = this.inputFormControl();
    if (this.loading()) {
      return;
    }

    const ref = afterNextRender(
      () => {
        control.setValue(control.value, { emitEvent: false });
      },
      { injector: this.injector },
    );
    onCleanup(() => ref.destroy());
  });

  private readonly reconcileSelectionAfterValueChange = effect((onCleanup) => {
    const control = this.inputFormControl();
    const sub = control.valueChanges.subscribe(() => {
      if (this.loading()) {
        return;
      }
      afterNextRender(
        () => {
          control.setValue(control.value, { emitEvent: false });
        },
        { injector: this.injector },
      );
    });
    onCleanup(() => sub.unsubscribe());
  });

  private readonly defaultCompareByIdOrStrict = (
    option: unknown,
    value: unknown,
  ): boolean => {
    if (option == null || value == null) {
      return option === value;
    }
    if (typeof option === 'object' && typeof value === 'object') {
      const optionId = (option as { id?: unknown }).id;
      const valueId = (value as { id?: unknown }).id;
      if (optionId != null && valueId != null) {
        return optionId === valueId;
      }
    }
    return option === value;
  };
}
